import React, { useEffect, useState, useRef } from "react";
import style from "./Activity.module.scss";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import Button from "../../../../components/new-button";
import SlidingTab from "../../../../components/SlidingTab/SlidingTab";
import ActivityRow from "./ActivityRow";
import Note from "./Note";
import {
  getNotes,
  getUserToMention,
  addNotes,
  undoNote,
} from "../../../../../api";
import DropDown from "../../../../components/DropDown/DropDown";
import { MentionsInput, Mention } from "react-mentions";
import toast from "react-hot-toast";
import UndoIcon from "../../../../assets/svg/icons/common/Undo";
import FileIcon from "../../../../assets/svg/icons/common/FileIcon";
import CrossIcon from "../../../../assets/svg/icons/common/cross";
import useCommonStore from "../../../../stores/common";
import useUserStore from "../../../../stores/user";
import CaretDown from '../../../../assets/svg/icons/common/CaretDown';
import PaperClipIcon from '../../../../assets/svg/icons/common/PaperClip';


function Activity({ activeApp }) {
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const toggleExpandDetails = useCommonStore((state) => state.toggleExpandDetails);
  const loggedInUser = useUserStore((state) => state.user);
  const [notes, setNotes] = useState([]);
  const [listingNotes, setListingNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});
  const sliding_tab_options = [
    "All",
    "PCI",
    "Agent",
    "Underwriting",
    "Merchant",
  ];
  const [activeTab, setActiveTab] = useState("Agent");
  const [selectNoteType, setSelectedNoteType] = useState("Agent");
  const [noteMentionUser, setNoteMentionUser] = useState([]);
  const [noteContent, setNoteContent] = useState("");
  const [usersToMention, setUsersToMention] = useState([]);
  const fileInputRef = useRef(null);
  const isPciAdmin =
    loggedInUser?.role == "admin" && loggedInUser?.organization?.id == 1;

  const onTabChange = (val) => {
    setActiveTab(val);
  };

  const loadNotes = () => {
    let data = {
      id: activeApp.id,
      parent_id: "",
    };

    getNotes(data)
      .then((res) => {
        // Process notes to organize parent notes and their replies
        const organizedNotes = organizeNotesWithReplies(res.notes);
        setNotes(organizedNotes);
        setListingNotes(organizedNotes);
        setActivities(res.system_notes);
      })
      .catch((err) => {
        console.error("Error loading notes:", err);
      });
  };

  // Organize notes to group parent notes with their replies
  const organizeNotesWithReplies = (notesList) => {
    // If we don't have a valid notes list, return an empty array
    if (!notesList || !Array.isArray(notesList) || notesList.length === 0) {
      return [];
    }

    // Create a map of parent notes and their children
    const parentNotes = [];
    const childNotesMap = {};

    // First, categorize notes as either parents or children
    notesList.forEach(note => {
      // A note is a parent if it has children array
      if (note.children && Array.isArray(note.children) && note.children.length > 0) {
        // Add to parent notes with its children as replies
        // Process the children to ensure they have all needed fields
        const processedChildren = note.children.map(child => {
          // Map child.notes_type to signal if needed
          let signal = child.signal;
          if (!signal && child.notes_type) {
            if (child.notes_type === 'agent_note') signal = 'warning';
            else if (child.notes_type === 'pci_note' || child.notes_type === 'admin_note' || child.notes_type === 'underwriting_note') signal = 'danger';
            else if (child.notes_type === 'merchant_note') signal = 'merchant';
          }

          // Return a well-formed child note
          return {
            ...child,
            signal: signal,
            // If child has no created_ago but has created_at, we'll let the Note component calculate it
            // If child has no who field, we'll let the Note component handle display name
          };
        });

        parentNotes.push({
          ...note,
          replies: processedChildren // Use the processed children array as replies
        });
      }
      // A note with parent_id is a reply/child
      else if (note.parent_id) {
        if (!childNotesMap[note.parent_id]) {
          childNotesMap[note.parent_id] = [];
        }
        childNotesMap[note.parent_id].push(note);
      }
      // A note without parent_id and without children is a standalone parent note
      else {
        parentNotes.push({
          ...note,
          replies: [] // Initialize empty replies array
        });
      }
    });

    // For backward compatibility, also attach any notes with parent_id
    // to their respective parents if they weren't already added via children array
    parentNotes.forEach(parentNote => {
      if (childNotesMap[parentNote.id]) {
        // If the parent already has replies from the children array, add any additional replies
        if (parentNote.replies && parentNote.replies.length > 0) {
          parentNote.replies = [
            ...parentNote.replies,
            ...childNotesMap[parentNote.id]
          ];
        } else {
          parentNote.replies = childNotesMap[parentNote.id];
        }
      }
    });

    return parentNotes;
  };

  useEffect(() => {
    loadNotes();
    userToMention();
  }, []);

  useEffect(() => {
    if (activeTab == "All") {
      setListingNotes(notes);
    } else {
      const mapping = {
        Merchant: "merchant",
        Agent: "warning",
        PCI: "danger",
        Underwriting: "underwriting"
      };

      // Filter parent notes by signal
      let filteredNotes = notes.filter(
        (note) => note.signal == mapping[activeTab]
      );

      // Also include parent notes that have replies matching the filter
      notes.forEach(note => {
        // If this parent note is not already included and has replies
        if (!filteredNotes.includes(note) && note.replies && note.replies.length > 0) {
          // Check if any reply matches the current filter
          const hasMatchingReplies = note.replies.some(reply => reply.signal === mapping[activeTab]);

          if (hasMatchingReplies) {
            // Add this parent note to the filtered list
            filteredNotes.push({
              ...note,
              // Only include the replies that match the filter
              replies: note.replies.filter(reply => reply.signal === mapping[activeTab])
            });
          }
        }
      });

      setListingNotes(filteredNotes);
    }
  }, [activeTab, notes]);

  const userToMention = () => {
    getUserToMention({}).then((response) => {
      if (response.success === true) {
        let mentionUserList = [];
        if (response.isPcAdmin === true) {
          response.data.forEach((data) => {
            let displayName =
              data.display + " (" + data.organization.name + ")";
            mentionUserList.push({
              id: data.id,
              display: displayName,
            });
          });
        } else {
          response.data.forEach((data) => {
            mentionUserList.push({
              id: data.id,
              display: data.display,
            });
          });
        }
        setUsersToMention(mentionUserList);
      } else {
        setUsersToMention([]);
      }
    });
  };

  const drop_down_options_for_admin = [
    {
      text: "Agent",
      type: "normal",
      onClick: () => {
        setSelectedNoteType("Agent");
      },
    },
    {
      text: "PCI",
      type: "normal",
      onClick: () => {
        setSelectedNoteType("PCI");
      },
    },
    {
      text: "Merchant",
      type: "normal",
      onClick: () => {
        setSelectedNoteType("Merchant");
      },
    },
  ];

  const drop_down_options = [
    {
      text: "Agent",
      type: "normal",
      onClick: () => {
        setSelectedNoteType("Agent");
      },
    },
  ];

  const handleNote = (content) => {
    setNoteContent(content);
  };

  const getMentions = (id, display, startPos, endPos) => {
    let mention = noteMentionUser;
    let mentionExist = mention.find((x) => x.id == id);
    if (mentionExist == undefined || mentionExist == false) {
      mention.push({ id: id, display: "@" + display });
      setNoteMentionUser(mention);
    }
  };

  const undoClick = (response, submit_data, toastId) => {
    undoNote({
      note_id: response.notes.id,
    })
      .then((res) => {
        toast.success("Undo Successful", {
          id: toastId,
          duration: 10 * 1000,
        });
        loadNotes();
        setNoteContent(submit_data.content);
      })
      .catch((err) => { });
  };

  const handleReplyClick = (note) => {
    setReplyTo(note);
  };

  const cancelReply = () => {
    setReplyTo(null);
    // Optionally clear the note content when canceling a reply
    // setNoteContent('');
  };

  const toggleReplies = (noteId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/gif'
    ];

    // Validate file types
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error('Only PDF, Excel, Word and Image files are allowed');
      e.target.value = '';
      return;
    }

    // Calculate remaining slots
    const remainingSlots = 3 - selectedFiles.length;
    if (remainingSlots <= 0) {
      toast.error('Maximum 3 files allowed');
      e.target.value = '';
      return;
    }

    // Take only the first N files that would fit within the 3-file limit
    const filesToAdd = files.slice(0, remainingSlots);

    if (files.length > remainingSlots) {
      toast.error(`Only ${remainingSlots} more file${remainingSlots > 1 ? 's' : ''} can be added`);
    }

    // Add files to state with additional metadata
    const filesWithMetadata = filesToAdd.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    }));

    setSelectedFiles(prevFiles => [...prevFiles, ...filesWithMetadata]);
    e.target.value = '';
  };

  const removeFile = (idToRemove) => {
    setSelectedFiles(prevFiles => prevFiles.filter(file => file.id !== idToRemove));
  };

  const onSend = async () => {
    if (selectedFiles.length === 0 && !noteContent.trim()) {
      toast.error('Please add a note or attach files');
      return;
    }

    setIsUploading(true);
    const toastId = toast.loading(replyTo ? "Adding reply..." : "Adding note...", { duration: 30 * 1000 });

    try {
      const formData = new FormData();

      // Add note content
      formData.append('content', noteContent);
      formData.append('stratus', true);

      // Add note type
      formData.append('add-agentnote', selectNoteType === 'Agent' ? 'agent_note' : '');
      formData.append('add-pcinote', selectNoteType === 'PCI' ? 'add-pcinote' : '');
      formData.append('add-merchantnote', selectNoteType === 'Merchant' ? 'add-merchantnote' : '');

      // Add mentioned users
      if (noteMentionUser.length > 0) {
        formData.append('note_mention_user', JSON.stringify(noteMentionUser));
      }

      // Store the parent ID for expanding replies later
      const parentId = replyTo ? replyTo.id : null;

      // Add parent_id if replying to a note
      if (replyTo) {
        formData.append('parent_id', replyTo.id);
      } else {
        formData.append('parent_id', '');
      }

      // Add files
      selectedFiles.forEach((fileData, index) => {
        formData.append(`files[]`, fileData.file);
      });

      const response = await addNotes(activeApp.id, formData);

      if (response.status) {
        // If this was a reply, make sure to expand the parent note's replies
        if (parentId) {
          setExpandedNotes(prev => ({
            ...prev,
            [parentId]: true
          }));
        }

        // Instead of relying on the automatic reload to show the new reply,
        // we can update the UI immediately for a better user experience
        if (replyTo && response.notes) {
          // Create a temporary representation of the new reply
          const newReply = {
            id: response.notes.id,
            content: noteContent,
            created_at: new Date().toISOString(),
            created_ago: "Just now",
            files: Array.isArray(selectedFiles) && selectedFiles.length > 0 ?
              selectedFiles.map(file => ({
                file_name: file.name,
                file_path: URL.createObjectURL(file.file)
              })) : null,
            notes_type: selectNoteType === 'Agent' ? 'agent_note' :
              selectNoteType === 'PCI' ? 'pci_note' :
                'merchant_note',
            parent_id: replyTo.id,
            who: loggedInUser?.name || "You"
          };

          // Update the notes state by finding the parent note and adding the reply
          const updatedNotes = notes.map(note => {
            if (note.id === replyTo.id) {
              return {
                ...note,
                replies: [...(note.replies || []), newReply]
              };
            }
            return note;
          });

          // Update both state variables
          setNotes(updatedNotes);

          // Apply the same filtering logic to the updated notes
          if (activeTab === "All") {
            setListingNotes(updatedNotes);
          } else {
            const mapping = {
              Merchant: "merchant",
              Agent: "warning",
              PCI: "danger",
              Underwriting: "underwriting"
            };

            // Filter notes based on the active tab
            const filteredNotes = updatedNotes.filter(
              note => note.signal === mapping[activeTab] ||
                (note.replies && note.replies.some(reply =>
                  reply.signal === mapping[activeTab] ||
                  (reply.notes_type === 'agent_note' && mapping[activeTab] === 'warning') ||
                  (reply.notes_type === 'pci_note' && mapping[activeTab] === 'danger') ||
                  (reply.notes_type === 'merchant_note' && mapping[activeTab] === 'merchant')
                ))
            );

            setListingNotes(filteredNotes);
          }
        }

        // Still reload notes from the server for data consistency
        loadNotes();
        setNoteContent('');
        setSelectedFiles([]);
        setReplyTo(null);
        toast.success(
          <div className={style.undo_container}>
            <span>{replyTo ? "Reply Added Successfully" : "Note Added Successfully"}</span>
            <button
              className={style.undo_button}
              onClick={() => undoClick(response, formData, toastId)}
            >
              <UndoIcon />
            </button>
          </div>,
          { id: toastId, duration: 30 * 1000 }
        );
      } else {
        toast.error(response.message, { duration: 10 * 1000, id: toastId });
      }
    } catch (err) {
      toast.error("Something went wrong!", {
        duration: 10 * 1000,
        id: toastId
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={style.activity}>
      <div
        className={`${style.note_section} ${expandDetails ? style.expanded : ""}`}
      >
        {!expandDetails && (
          <div className={style.alert}>
            <div className={style.text_content}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M14.7081 7.29454L8.705 1.29079C8.51764 1.10454 8.26419 1 8 1C7.73582 1 7.48237 1.10454 7.295 1.29079L1.295 7.29454C1.10875 7.48191 1.00421 7.73536 1.00421 7.99954C1.00421 8.26373 1.10875 8.51718 1.295 8.70454L7.29813 14.7083C7.48549 14.8945 7.73894 14.9991 8.00313 14.9991C8.26732 14.9991 8.52077 14.8945 8.70813 14.7083L14.7113 8.70454C14.8975 8.51718 15.002 8.26373 15.002 7.99954C15.002 7.73536 14.8975 7.48191 14.7113 7.29454H14.7081ZM7.5 4.99954C7.5 4.86693 7.55268 4.73976 7.64645 4.64599C7.74022 4.55222 7.8674 4.49954 8 4.49954C8.13261 4.49954 8.25979 4.55222 8.35356 4.64599C8.44733 4.73976 8.5 4.86693 8.5 4.99954V8.49954C8.5 8.63215 8.44733 8.75933 8.35356 8.8531C8.25979 8.94686 8.13261 8.99954 8 8.99954C7.8674 8.99954 7.74022 8.94686 7.64645 8.8531C7.55268 8.75933 7.5 8.63215 7.5 8.49954V4.99954ZM8 11.4995C7.85167 11.4995 7.70666 11.4556 7.58333 11.3731C7.45999 11.2907 7.36386 11.1736 7.30709 11.0366C7.25033 10.8995 7.23548 10.7487 7.26442 10.6032C7.29335 10.4577 7.36478 10.3241 7.46967 10.2192C7.57456 10.1143 7.7082 10.0429 7.85369 10.014C7.99917 9.98501 8.14997 9.99987 8.28702 10.0566C8.42406 10.1134 8.54119 10.2095 8.62361 10.3329C8.70602 10.4562 8.75 10.6012 8.75 10.7495C8.75 10.9485 8.67099 11.1392 8.53033 11.2799C8.38968 11.4205 8.19892 11.4995 8 11.4995Z"
                  fill="#C58300"
                />
              </svg>
              <span>Activity is hidden in minimized view</span>
            </div>
            <ButtonIcon
              textColor="#254FE2"
              bgColor="#FFF7DB"
              onClick={() => toggleExpandDetails()}
            >
              Expand Details
            </ButtonIcon>
          </div>
        )}

        <div className={style.head}>
          <h6>Notes</h6>
          <div>
            {isPciAdmin && (
              <SlidingTab
                options={sliding_tab_options}
                onChange={onTabChange}
                selectedTab={activeTab}
              />
            )}
          </div>
        </div>

        <div className={style.notes_container}>
          {/* <p className={style.month_title} >October 2024</p> */}

          {listingNotes?.map((note, index) => (
            <React.Fragment key={index}>
              <Note
                note={note}
                onReplyClick={handleReplyClick}
              />

              <div className={`${style.replies} ${note.replies && Array.isArray(note.replies) && note.replies.length > 0 ? style.expanded : ""}`}>
                {/* Render only the first reply by default if there are replies */}
                {note.replies && Array.isArray(note.replies) && note.replies.length > 0 && (
                  <Note
                    key={`reply-0`}
                    note={note.replies[0]}
                    isReply={true}
                    showReplyButton={false}
                  />
                )}

                {/* Render additional replies when expanded */}
                {expandedNotes[note.id] && note.replies && Array.isArray(note.replies) && note.replies.length > 1 &&
                  note.replies.slice(1).map((reply, replyIndex) => (
                    <Note
                      key={`reply-${replyIndex + 1}`}
                      note={reply}
                      isReply={true}
                      showReplyButton={false}
                    />
                  ))
                }

                {/* Show "Show more replies" button if there are more than 1 reply */}
                {note.replies && Array.isArray(note.replies) && note.replies.length > 1 && (
                  <div className={style.showMoreReplies}>
                    <button
                      className={style.showMoreRepliesButton}
                      onClick={() => toggleReplies(note.id)}
                      data-expanded={expandedNotes[note.id] ? "true" : "false"}
                    >
                      {expandedNotes[note.id] ? 'Hide replies' : `Show ${note.replies.length - 1} more ${note.replies.length - 1 === 1 ? 'reply' : 'replies'}`}
                    </button>
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className={style.footer}>
          {replyTo && (
            <div className={style.replyPreview}>
              <div className={style.replyPreviewHeader}>
                <span className={style.replyToText}>Reply to {replyTo.who}</span>
                <button
                  className={style.closeReplyButton}
                  onClick={cancelReply}
                  title="Cancel reply"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12.5 3.5L3.5 12.5M3.5 3.5L12.5 12.5" stroke="#3A383F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              <div className={style.replyPreviewContent}>
                {replyTo.content}
              </div>
            </div>
          )}

          {selectedFiles.length > 0 && (
            <div className={style.selectedFiles}>
              {selectedFiles.map((fileData) => (
                <div
                  key={fileData.id}
                  className={style.file_item}
                  title={fileData.name}
                >
                  <FileIcon color="#E03B24" />
                  <div className={style.file_info}>
                    <span className={style.file_name}>{fileData.name}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="small"
                    icon={<CrossIcon />}
                    onClick={() => removeFile(fileData.id)}
                    disabled={isUploading}
                  />
                </div>
              ))}
            </div>
          )}

          <input type="file"
            className={style.file_input}
            id="file-input"
            ref={fileInputRef}
            multiple
            accept=".pdf,.xls,.xlsx,.doc,.docx,.jpg,.jpeg,.png,.gif"
            onChange={handleFileUpload}
            disabled={isUploading}
          />

          <div className={style.footer_content}>
            <MentionsInput
              value={noteContent}
              onChange={(e, newValue, newPlainTextValue, mentions) =>
                handleNote(newPlainTextValue)
              }
              className={style.note_input}
              name="note"
              placeholder={replyTo ? "Write a reply..." : "Add a Note"}
            >
              <Mention
                trigger="@"
                data={usersToMention}
                appendSpaceOnAdd={true}
                displayTransform={(id, display) => `@${display}`}
                onAdd={(id, display, startPos, endPos) =>
                  getMentions(id, display, startPos, endPos)
                }
              />
            </MentionsInput>

            <div className={style.footer_actions}>
              <Button
                variant="ghost"
                icon={
                  <PaperClipIcon />
                }
                onClick={() => fileInputRef.current?.click()}
              >
              </Button>

              <DropDown
                options={
                  isPciAdmin ? drop_down_options_for_admin : drop_down_options
                }
                bgColor="#EDEDF4"
                direction="up"
              >
                <span>{selectNoteType}</span>
                <CaretDown />
              </DropDown>

              <Button
                bgColor="#4C70EF"
                onClick={onSend}
                disabled={noteContent !== "" ? false : true}
                variant="primary"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M15 7.99327C15.0005 8.17144 14.9533 8.34649 14.8634 8.50033C14.7735 8.65416 14.6442 8.78119 14.4888 8.86827L3.99375 14.8689C3.84315 14.9543 3.67312 14.9995 3.5 15.0001C3.3405 14.9993 3.18351 14.9603 3.04215 14.8864C2.90078 14.8125 2.77915 14.7059 2.68739 14.5754C2.59564 14.4449 2.53643 14.2944 2.5147 14.1364C2.49297 13.9783 2.50936 13.8174 2.5625 13.667L4.25 8.67014C4.26649 8.62129 4.29769 8.57873 4.33931 8.5483C4.38093 8.51786 4.43095 8.50104 4.4825 8.50014H9C9.06855 8.50029 9.13639 8.48635 9.19932 8.45918C9.26224 8.432 9.31891 8.39218 9.3658 8.34219C9.41269 8.29219 9.4488 8.23309 9.47189 8.16855C9.49497 8.10401 9.50454 8.03541 9.5 7.96702C9.48867 7.83845 9.42917 7.7189 9.33345 7.63233C9.23772 7.54575 9.11281 7.49854 8.98375 7.50014H4.48375C4.43145 7.50015 4.38046 7.48375 4.33797 7.45325C4.29547 7.42275 4.26361 7.3797 4.24688 7.33014L2.55938 2.33389C2.49221 2.14239 2.4849 1.93498 2.53842 1.73922C2.59194 1.54346 2.70375 1.36862 2.859 1.23792C3.01425 1.10723 3.20559 1.02686 3.40761 1.00749C3.60962 0.98813 3.81275 1.03069 3.99 1.12952L14.49 7.12264C14.6446 7.20952 14.7732 7.33593 14.8629 7.48892C14.9525 7.6419 14.9998 7.81597 15 7.99327Z"
                      fill="white"
                    />
                  </svg>
                }
              >
              </Button>
            </div>
          </div>
        </div>
      </div>

      {expandDetails && (
        <div className={style.activity_section}>
          <div className={style.card}>
            <div className={style.activity_head}>
              <h6>Activity</h6>
              {/* <SearchInput /> */}
            </div>

            <div className={style.activity_content}>
              {activities.map((activity, index) => (
                <ActivityRow key={index} activity={activity} />
              ))}
            </div>

            {/* <Pagination /> */}
          </div>
        </div>
      )}
    </div>
  );
}

export default Activity;
